/**
 * Production adapter for the PACS patient tools — binds the React-free ports
 * to the app: the connector's existing generated fetchers (the same routes
 * the DICOM import dialog uses), the agent import-request atom, and the
 * dialog's open flag. Like the other ops' engineDeps, this browser glue is
 * covered by the manual demo; the logic it feeds is unit-tested through
 * fakes.
 */
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import {
    fetchEcho,
    fetchGetDicomPatients,
    fetchGetDicomPatientStudies,
    fetchGetPatient,
    fetchGetPatients,
    fetchListTargets,
} from '~/backend/connector-api/connector-components';
import type { DICOMPatientPresentation } from '~/backend/connector-api/connector-schemas';
import { agentDicomImportRequestState } from '~/state/atoms/chatAtoms';
import { dicomSourcesModalOpenState } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import type { ConnectorStoreDeps } from './connectorStore';
import type { PatientDataDeps } from './patientQueries';
import type { LoadSeriesDeps } from './toolRunner';

export type PatientEngineDeps = PatientDataDeps &
    ConnectorStoreDeps &
    Omit<LoadSeriesDeps, 'pendingMutationExists'>;

const patientDataDeps: PatientDataDeps = {
    get connectorMode() {
        return AppConfig.isConnectorMode;
    },
    searchPatients: async (term) =>
        await fetchGetDicomPatients({ queryParams: { patientIDTerm: term } }),
    getPatientStudies: async (patientId, targetName) => {
        const data: DICOMPatientPresentation[] | DICOMPatientPresentation =
            await fetchGetDicomPatientStudies({
                pathParams: { patientID: patientId },
                queryParams: { targetName },
            });
        // The route returns an array (one patient); tolerate a bare object,
        // as the import dialog does.
        return Array.isArray(data) ? data[0] : data;
    },
    inboundTargets: async () => {
        const targets = await fetchListTargets({});
        return Object.entries(targets ?? {})
            .filter(([, dir]) => dir === 'In' || dir === 'InOut')
            .map(([name]) => name);
    },
};

const connectorStoreDeps: Omit<ConnectorStoreDeps, 'connectorMode'> = {
    listImportedPatients: async () => (await fetchGetPatients({})) ?? [],
    getImportedPatient: async (patientId) =>
        await fetchGetPatient({ pathParams: { patientId } }),
    listTargets: async () => (await fetchListTargets({})) ?? {},
    echoAllTargets: async () => {
        await fetchEcho({});
    },
};

export function usePatientDeps(): () => PatientEngineDeps {
    const setRequest = useSetRecoilState(agentDicomImportRequestState);
    const setDialogOpen = useSetRecoilState(dicomSourcesModalOpenState);

    return useCallback(
        (): PatientEngineDeps => ({
            connectorMode: patientDataDeps.connectorMode,
            searchPatients: patientDataDeps.searchPatients,
            getPatientStudies: patientDataDeps.getPatientStudies,
            inboundTargets: patientDataDeps.inboundTargets,
            listImportedPatients: connectorStoreDeps.listImportedPatients,
            getImportedPatient: connectorStoreDeps.getImportedPatient,
            listTargets: connectorStoreDeps.listTargets,
            echoAllTargets: connectorStoreDeps.echoAllTargets,
            requestImport: (request) => setRequest(request),
            openImportDialog: () => setDialogOpen(true),
        }),
        [setRequest, setDialogOpen]
    );
}
